import React, { useState, useEffect, useContext, useRef } from "react";
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, ClickAwayListener, Grow, Tooltip, Badge } from "@mui/material";
import { IPlayerState } from '../../../Core/State/IPlayerState';
import { PokerTheme } from '../../../Themes/PokerThemes';
import {
    Close as CloseIcon,
    History as HistoryIcon,
    Info as InfoIcon,
    NotificationsActive as ActivityIcon,
    Casino as DealerIcon,
    Person as PlayerIcon,
    AttachMoney as PotIcon,
    Timeline as RoundIcon,
    AccessTime as TurnIcon,
    LocalAtm as BetIcon,
    Stars as BlindIcon,
    CallMade as RaiseIcon,
    Block as FoldIcon,
    Check as CheckIcon,
    MonetizationOn as AnteIcon,
    AllInclusive as AllInIcon,
    SubdirectoryArrowRight as CallIcon,
    Insights as InsightsIcon
} from "@mui/icons-material";
import styles from '../../../assets/css/components/Aof.module.css';
import gameInfoStyles from '../../../assets/css/components/GameInfo.module.css';
import { debounce } from 'lodash';
import { TemplateUtil, ActivityMessage } from '../../../Utils/TemplateUtil';
import { DetachedWindowContext } from '../WindowPortal';
import { t } from '../../../i18n';

export interface GameInfoTurnHistoryEntry {
    time: string;
    username: string;
    selectedAction: {
        action: string;
        amount: number;
    };
}

interface GameInfoProps {
    players: IPlayerState[];
    pot: number;
    currentBet: number;
    roundName: string | null;
    activePlayerId: string | null;
    dealerId: string | null;
    bigBlindId: string | null;
    smallBlindId: string | null;
    formatCurrency: (num: number) => string;
    activityMessages: ActivityMessage[];
    playerTurnHistory: GameInfoTurnHistoryEntry[];
    renderMessageWithCards: (message: string) => React.ReactNode;
    onCollapseChange: (isExpanded: boolean) => void;
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
    return (
        <div role="tabpanel" hidden={value !== index}>
            {value === index && <Box p={0}>{children}</Box>}
        </div>
    );
};

const generateColorFromString = (str: string) => {
    const colors = [
        '#19e5e0',  // cyan
        '#f6c944',  // gold
        '#ff9f64',  // orange
        '#9147ff',  // purple
        '#3de361',  // green
        '#ff64b1',  // pink
        '#64d2ff',  // light blue
        '#ff6464',  // coral
    ];
    
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    return colors[Math.abs(hash) % colors.length];
};

const formatActionText = (action: string) => {
    if (action.toLowerCase() === 'post_small_blind') {
        return t('GameRoom:gameInfo.labels.postSmallBlind');
    }
    if (action.toLowerCase() === 'post_big_blind') {
        return t('GameRoom:gameInfo.labels.postBigBlind');
    }

    return action
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
};

const checkScrollable = (element: HTMLDivElement | null) => {
    if (!element) return;
    
    const isScrollable = element.scrollHeight > element.clientHeight;
    if (element.getAttribute('data-scrollable') !== isScrollable.toString()) {
        element.setAttribute('data-scrollable', isScrollable.toString());
    }
};

interface PillTabProps {
    icon: React.ReactNode;
    label: string;
    isActive: boolean;
    onClick: () => void;
}

const PillTab: React.FC<PillTabProps> = ({ icon, label, isActive, onClick }) => (
    <Box
        role="tab"
        aria-selected={isActive}
        onClick={onClick}
        className={`${gameInfoStyles.pillTab} ${isActive ? gameInfoStyles.pillTabActive : ''}`}
    >
        <Box component="span" className={gameInfoStyles.pillTabIcon}>{icon}</Box>
        <Box component="span" className={gameInfoStyles.pillTabLabel}>{label}</Box>
    </Box>
);

const GameInfo: React.FC<GameInfoProps> = ({
    players,
    pot,
    currentBet,
    roundName,
    activePlayerId,
    dealerId,
    bigBlindId,
    smallBlindId,
    formatCurrency,
    activityMessages,
    playerTurnHistory,
    renderMessageWithCards,
    onCollapseChange
}) => {
    // Get detached window context
    const detachedWindow = useContext(DetachedWindowContext);
    const isDetached = !!detachedWindow;
    
    const [tabIndex, setTabIndex] = useState<number>(0);
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [unseenCount, setUnseenCount] = useState<number>(0);
    const lastSeenActivityRef = useRef<number>(activityMessages.length);

    const activityMessagesRef = React.useRef<HTMLDivElement>(null);
    const playerTurnHistoryRef = React.useRef<HTMLDivElement>(null);
    const gameInfoRef = React.useRef<HTMLDivElement>(null);

    // Use refs to track if auto-scroll is needed
    const shouldScrollActivityRef = React.useRef<boolean>(true);
    const shouldScrollHistoryRef = React.useRef<boolean>(true);
    
    // Debounced scroll handlers with null check
    const handleActivityScroll = React.useCallback((event: React.UIEvent<HTMLDivElement>) => {
        if (!event.currentTarget) return;
        
        const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
        const isAtBottom = Math.abs(scrollHeight - clientHeight - scrollTop) < 1;
        shouldScrollActivityRef.current = isAtBottom;
    }, []);

    const handleHistoryScroll = React.useCallback((event: React.UIEvent<HTMLDivElement>) => {
        if (!event.currentTarget) return;
        
        const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
        const isAtBottom = Math.abs(scrollHeight - clientHeight - scrollTop) < 1;
        shouldScrollHistoryRef.current = isAtBottom;
    }, []);

    const debouncedActivityScroll = React.useMemo(
        () => debounce(handleActivityScroll, 150),
        [handleActivityScroll]
    );

    const debouncedHistoryScroll = React.useMemo(
        () => debounce(handleHistoryScroll, 150),
        [handleHistoryScroll]
    );

    // Cleanup debounced functions
    useEffect(() => {
        return () => {
            debouncedActivityScroll.cancel();
            debouncedHistoryScroll.cancel();
        };
    }, [debouncedActivityScroll, debouncedHistoryScroll]);

    // Optimized scroll to bottom function
    const scrollToBottom = React.useCallback((element: HTMLDivElement | null) => {
        if (element) {
            requestAnimationFrame(() => {
                element.scrollTop = element.scrollHeight;
            });
        }
    }, []);

    const handleTabChange = (newValue: number) => {
        setTabIndex(newValue);
    };

    const togglePanel = () => {
        const newState = !isOpen;
        setIsOpen(newState);
        onCollapseChange(newState);
        if (newState) {
            lastSeenActivityRef.current = activityMessages.length;
            setUnseenCount(0);
        }
    };

    const handleClickAway = () => {
        if (isOpen) {
            setIsOpen(false);
            onCollapseChange(false);
        }
    };

    // Track unseen activity when panel is closed
    useEffect(() => {
        if (!isOpen) {
            const delta = activityMessages.length - lastSeenActivityRef.current;
            if (delta > 0) {
                setUnseenCount((prev) => Math.min(prev + delta, 99));
            }
            lastSeenActivityRef.current = activityMessages.length;
        } else {
            lastSeenActivityRef.current = activityMessages.length;
        }
    }, [activityMessages.length, isOpen]);

    // Auto-scroll effects
    useEffect(() => {
        if (shouldScrollActivityRef.current && tabIndex === 0) {
            scrollToBottom(activityMessagesRef.current);
        }
    }, [activityMessages, tabIndex, scrollToBottom]);

    useEffect(() => {
        if (shouldScrollHistoryRef.current && tabIndex === 1) {
            scrollToBottom(playerTurnHistoryRef.current);
        }
    }, [playerTurnHistory, tabIndex, scrollToBottom]);

    // Scroll optimization
    useEffect(() => {
        const elements = [
            activityMessagesRef.current,
            playerTurnHistoryRef.current,
            gameInfoRef.current
        ].filter(Boolean);

        const observer = new ResizeObserver(
            debounce((entries: ResizeObserverEntry[]) => {
                entries.forEach((entry: ResizeObserverEntry) => {
                    if (entry.target) {
                        checkScrollable(entry.target as HTMLDivElement);
                    }
                });
            }, 100)
        );

        elements.forEach(element => {
            if (element) observer.observe(element);
        });

        return () => observer.disconnect();
    }, []);

    const getActionIcon = (action: string) => {
        const actionClass = `${gameInfoStyles.actionIcon} `;
        switch (action.toLowerCase()) {
            case 'raise':
                return <RaiseIcon className={actionClass + gameInfoStyles.raiseAction} />;
            case 'call':
                return <CallIcon className={actionClass + gameInfoStyles.callAction} />;
            case 'fold':
                return <FoldIcon className={actionClass + gameInfoStyles.foldAction} />;
            case 'check':
                return <CheckIcon className={actionClass + gameInfoStyles.checkAction} />;
            case 'ante':
                return <AnteIcon className={actionClass + gameInfoStyles.anteAction} />;
            case 'all-in':
                return <AllInIcon className={actionClass + gameInfoStyles.allInAction} />;
            default:
                return <HistoryIcon className={actionClass + gameInfoStyles.historyIcon} />;
        }
    };

    const getActionColor = (action: string) => {
        switch (action.toLowerCase()) {
            case 'raise':
                return gameInfoStyles.raiseAction;
            case 'call':
                return gameInfoStyles.callAction;
            case 'fold':
                return gameInfoStyles.foldAction;
            case 'check':
                return gameInfoStyles.checkAction;
            case 'ante':
                return gameInfoStyles.anteAction;
            case 'all-in':
                return gameInfoStyles.allInAction;
            default:
                return gameInfoStyles.defaultAction;
        }
    };

    // Limit the number of messages and history entries to 40
    const limitedActivityMessages = activityMessages.slice(-40);
    const limitedPlayerTurnHistory = playerTurnHistory.slice(-40);

    return (
        <ClickAwayListener onClickAway={handleClickAway}>
            <Box
                className={gameInfoStyles.iconRoot}
                data-open={isOpen}
                data-detached={isDetached ? "true" : "false"}
            >
                {/* Trigger icon button */}
                <Tooltip
                    title={isOpen ? '' : t('GameRoom:gameInfo.tabs.gameInfo')}
                    placement="right"
                    arrow
                >
                    <IconButton
                        className={`${gameInfoStyles.triggerButton} ${isOpen ? gameInfoStyles.triggerButtonActive : ''}`}
                        onClick={togglePanel}
                        aria-label="toggle game info"
                    >
                        <Badge
                            badgeContent={unseenCount}
                            invisible={isOpen || unseenCount === 0}
                            overlap="circular"
                            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                            classes={{ badge: gameInfoStyles.unseenBadge }}
                        >
                            <InsightsIcon className={gameInfoStyles.triggerIcon} />
                        </Badge>
                    </IconButton>
                </Tooltip>

                {/* Popover panel */}
                <Grow in={isOpen} timeout={220} style={{ transformOrigin: 'bottom left' }} unmountOnExit>
                    <Box
                        className={`${styles.tabsContainer} ${gameInfoStyles.tabsContainer} ${gameInfoStyles.popoverPanel}`}
                        data-expanded={isOpen}
                        data-detached={isDetached ? "true" : "false"}
                    >
                        {/* Header with pill tabs and close button */}
                        <Box className={gameInfoStyles.panelHeader}>
                            <Box className={gameInfoStyles.pillTabs} role="tablist">
                                <PillTab
                                    icon={<ActivityIcon className={gameInfoStyles.tabIcon} />}
                                    label={t('GameRoom:gameInfo.tabs.activity')}
                                    isActive={tabIndex === 0}
                                    onClick={() => handleTabChange(0)}
                                />
                                <PillTab
                                    icon={<HistoryIcon className={gameInfoStyles.tabIcon} />}
                                    label={t('GameRoom:gameInfo.tabs.turnHistory')}
                                    isActive={tabIndex === 1}
                                    onClick={() => handleTabChange(1)}
                                />
                                <PillTab
                                    icon={<InfoIcon className={gameInfoStyles.tabIcon} />}
                                    label={t('GameRoom:gameInfo.tabs.gameInfo')}
                                    isActive={tabIndex === 2}
                                    onClick={() => handleTabChange(2)}
                                />
                            </Box>
                            <IconButton
                                onClick={togglePanel}
                                className={gameInfoStyles.closeButton}
                                aria-label="close game info"
                                size="small"
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Box>

                        {/* Activity Messages */}
                        <TabPanel value={tabIndex} index={0}>
                    <Box
                        ref={activityMessagesRef}
                        className={`${styles.customScrollbar} ${gameInfoStyles.contentPanel} ${gameInfoStyles.activityMessagesPanel}`}
                        onScroll={debouncedActivityScroll}
                        sx={(theme) => ({
                            backgroundColor: isDetached ? '#24242F' : (theme as PokerTheme).poker.activityMessagesBg || '#24242F',
                            color: isDetached ? 'white' : (theme as PokerTheme).poker.activityMessagesColor || 'white',
                        })}
                    >
                        {limitedActivityMessages.map((message: ActivityMessage, index) => (
                            <React.Fragment key={index}>
                                {TemplateUtil.renderActivityMessage(message)}
                            </React.Fragment>
                        ))}
                    </Box>
                </TabPanel>

                {/* Turn History */}
                <TabPanel value={tabIndex} index={1}>
                    <Box
                        className={`${styles.customScrollbar} ${gameInfoStyles.contentPanel}`}
                        onScroll={debouncedHistoryScroll}
                        sx={(theme) => ({
                            backgroundColor: isDetached ? '#24242F' : (theme as PokerTheme).poker.tableDataBg || '#24242F',
                            color: isDetached ? 'white' : (theme as PokerTheme).poker.tableDataColor || 'white',
                        })}
                    >
                        {limitedPlayerTurnHistory.map((entry, index) => (
                            <Box 
                                key={index} 
                                className={`${styles.handHistoryRow} ${gameInfoStyles.historyRow}`}
                            >
                                <Box className={gameInfoStyles.historyRowIndex}>
                                    #{index + 1}
                                </Box>
                                
                                <Box className={gameInfoStyles.historyRowUsername}>
                                    <Typography 
                                        component="span" 
                                        className={gameInfoStyles.commonText}
                                        style={{ color: generateColorFromString(entry.username) }}
                                    >
                                        {entry.username}
                                    </Typography>
                                </Box>
                                
                                <Box className={gameInfoStyles.historyRowAction}>
                                    {getActionIcon(entry.selectedAction.action)}
                                    <Typography 
                                        component="span" 
                                        className={`${gameInfoStyles.commonText} ${getActionColor(entry.selectedAction.action)}`}
                                    >
                                        {formatActionText(entry.selectedAction.action)}
                                    </Typography>
                                </Box>

                                <Box className={gameInfoStyles.historyRowAmount}>
                                    <Typography 
                                        component="span" 
                                        className={`${gameInfoStyles.commonText} ${gameInfoStyles.balanceText}`}
                                    >
                                        {formatCurrency(entry.selectedAction.amount)}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </TabPanel>

                {/* Game Info Table */}
                <TabPanel value={tabIndex} index={2}>
                    <Box
                        ref={gameInfoRef}
                        className={`${styles.customScrollbar} ${gameInfoStyles.contentPanel}`}
                        sx={(theme) => ({
                            backgroundColor: isDetached ? '#24242F' : (theme as PokerTheme).poker.tableDataBg || '#24242F',
                            color: isDetached ? 'white' : (theme as PokerTheme).poker.tableDataColor || 'white',
                        })}
                    >
                        <TableContainer 
                            component={Paper} 
                            className={gameInfoStyles.tableContainer}
                            sx={(theme) => ({
                                backgroundColor: (theme as PokerTheme).poker.tableDataBg || '#24242F',
                                color: (theme as PokerTheme).poker.tableDataColor || 'white',
                            })}
                        >
                            <Table 
                                size="small" 
                                aria-label="game info table"
                                className={gameInfoStyles.table}
                            >
                                <TableHead>
                                    <TableRow>
                                        <TableCell className={gameInfoStyles.tableCellHeader}>
                                            <PlayerIcon className={gameInfoStyles.statusIcon} sx={{ marginRight: 1 }} />
                                            {t('GameRoom:gameInfo.columns.player')}
                                        </TableCell>
                                        <TableCell className={gameInfoStyles.tableCellHeader} align="right">{t('GameRoom:gameInfo.columns.balance')}</TableCell>
                                        <TableCell className={gameInfoStyles.tableCellHeader} align="right">{t('GameRoom:gameInfo.columns.bet')}</TableCell>
                                        <TableCell className={gameInfoStyles.tableCellHeader} align="right">{t('GameRoom:gameInfo.columns.status')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {players.map((player) => (
                                        <TableRow key={player.userId} className={gameInfoStyles.tableRow}>
                                            <TableCell className={gameInfoStyles.tableCell} component="th" scope="row">
                                                <Typography 
                                                    className={`${gameInfoStyles.commonText} ${gameInfoStyles.usernameText}`}
                                                    style={{ color: generateColorFromString(player.username) }}
                                                >
                                                    {player.username}
                                                </Typography>
                                            </TableCell>
                                            <TableCell className={gameInfoStyles.tableCell} align="right">
                                                <Typography className={`${gameInfoStyles.commonText} ${gameInfoStyles.balanceText}`}>
                                                    {formatCurrency(player.balance || 0)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell className={gameInfoStyles.tableCell} align="right">
                                                <Typography className={`${gameInfoStyles.commonText} ${gameInfoStyles.balanceText}`}>
                                                    {formatCurrency(player.currentBet || 0)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell className={gameInfoStyles.tableCell} align="right">
                                                <Typography className={`${gameInfoStyles.commonText} ${gameInfoStyles.statusText} ${player.isPlayingCurrentGame ? gameInfoStyles.activeStatus : gameInfoStyles.waitingStatus}`}>
                                                    {player.isPlayingCurrentGame ? t('GameRoom:gameInfo.status.active') : t('GameRoom:gameInfo.status.waiting')}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>

                            {/* Game Status Section */}
                            <Box className={gameInfoStyles.gameStatusSection}>
                                {[
                                    { icon: <RoundIcon />, key: 'round', label: t('GameRoom:gameInfo.labels.round'), value: roundName },
                                    { icon: <TurnIcon />, key: 'turn', label: t('GameRoom:gameInfo.labels.turn'), value: players.find(p => p.userId === activePlayerId)?.username },
                                    { icon: <PotIcon />, key: 'pot', label: t('GameRoom:gameInfo.labels.pot'), value: formatCurrency(pot) },
                                    { icon: <BetIcon />, key: 'maxBet', label: t('GameRoom:gameInfo.labels.maxBet'), value: formatCurrency(currentBet) },
                                    { icon: <DealerIcon />, key: 'dealer', label: t('GameRoom:gameInfo.labels.dealer'), value: players.find(p => p.userId === dealerId)?.username },
                                    { icon: <BlindIcon />, key: 'bigBlind', label: t('GameRoom:gameInfo.labels.bigBlind'), value: players.find(p => p.userId === bigBlindId)?.username },
                                    { icon: <BlindIcon />, key: 'smallBlind', label: t('GameRoom:gameInfo.labels.smallBlind'), value: players.find(p => p.userId === smallBlindId)?.username },
                                ].map((row) => (
                                    <Box key={row.key} className={gameInfoStyles.gameStatusRow}>
                                        {React.cloneElement(row.icon, { className: gameInfoStyles.statusIcon })}
                                        <Typography className={`${gameInfoStyles.commonText} ${gameInfoStyles.gameStatusLabel}`}>
                                            {row.label}
                                        </Typography>
                                        <Typography
                                            className={gameInfoStyles.commonText}
                                            style={{
                                                color: row.key === 'turn' || row.key === 'dealer' || row.key.includes('Blind')
                                                    ? generateColorFromString(row.value as string || '')
                                                    : row.key === 'pot' || row.key === 'maxBet'
                                                    ? '#3de361'
                                                    : 'white',
                                                fontWeight: 500
                                            }}
                                        >
                                            {row.value != null ? row.value.toString() : t('GameRoom:gameInfo.labels.na')}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </TableContainer>
                    </Box>
                        </TabPanel>
                    </Box>
                </Grow>
            </Box>
        </ClickAwayListener>
    );
};

export default React.memo(GameInfo);
